import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import Papa from 'papaparse';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { generalConfigApi } from '../../apis';
import { DATE_FORMAT } from '../../constants/common';
import { QUERY_KEY } from '../../constants/QueryKey';
import { downloadCSV } from '../../utils';
import ButtonWrap from '../ButtonWrap';
import SvgUpload from '../svg/SvgUpload';
import TableWrap from '../TableWrap';
import ToastError from '../Toast/ToastError';
import './style.scss';

interface ImportCSVProps {
  dataCSVImportError?: string;
  type: 'USER' | 'WORKING_HOURS';
  onUploadFile: (items: unknown[]) => void;
  onConvertDataFile: (fileData: unknown[], keyMapping?: Object) => unknown[];
}

// Allowed extensions for input file
const allowedExtensions = ['text/csv', 'application/vnd.ms-excel'];

const ImportCSV: FC<ImportCSVProps> = (props) => {
  const { dataCSVImportError, type, onUploadFile, onConvertDataFile } = props;

  const intl = useIntl();

  const [dataCSV, setDataCSV] = useState<unknown[]>([]);
  const [fileName, setFileName] = useState<string>('');

  const { data: importTableData } = useQuery({
    queryKey: [QUERY_KEY.DATA_IMPORT_TABLE],
    queryFn: () => generalConfigApi.generalConfigControllerGetImportTableData(type),
  });

  const columns = importTableData?.data?.data?.columns;
  const dataSource = dataCSV?.map((data: any, index) => ({ ...data, index: index + 1 }));
  const keyMapping = importTableData?.data?.data?.keyMapping;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file: File | undefined = event?.target?.files?.[0];
    if (!file) {
      return;
    }

    const { name, type } = file;

    if (!allowedExtensions.includes(type)) {
      setFileName('');
      setDataCSV([]);
      onUploadFile([]);
      ToastError({ content: intl.formatMessage({ id: 'common.csv.import.failed' }) });
      return;
    }

    setFileName(name);

    const reader = new FileReader();

    reader.onload = () => {
      if (reader.result) {
        Papa.parse(reader.result, {
          header: true,
          complete: (results: Papa.ParseResult<unknown>) => {
            const convertData = onConvertDataFile(results.data, keyMapping);

            setDataCSV(convertData || []);
            onUploadFile(convertData);
          },
        });
      } else {
        ToastError({ content: intl.formatMessage({ id: 'common.csv.import.failed' }) });
      }
    };

    if (reader && file) {
      reader.readAsText(file);
    }
  };

  const handleDownloadCSV = () => {
    downloadCSV(dataCSVImportError, `employees_error_${dayjs().format(DATE_FORMAT)}.csv`);
  };

  return (
    <div className="import-csv">
      <div className="import-header">
        <label htmlFor="csvInput" className="btn-upload-csv">
          <SvgUpload />
          {intl.formatMessage({ id: 'common.chooseFile' })}
        </label>
        <div className="file-name">{fileName}</div>
        <input
          onChange={handleFileChange}
          id="csvInput"
          accept=".csv"
          name="file"
          type="file"
          className="custom-file-input"
        />
      </div>

      {dataCSVImportError && (
        <ButtonWrap variant="text" color="danger" onClick={handleDownloadCSV}>
          {intl.formatMessage({ id: 'common.csv.import.failed.download' })}
        </ButtonWrap>
      )}

      <TableWrap
        isPagination={false}
        loading={false}
        tableProps={{
          columns,
          dataSource,
          className: 'custom-table-import',
          scroll: { x: true },
        }}
      />
    </div>
  );
};

export default ImportCSV;
